import { useRealtimeKitMeeting, useRealtimeKitSelector } from '@cloudflare/realtimekit-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import DyteAudioHandler from 'utils/DyteAudioHandler';
import { shouldIncludeAudioForParent } from 'utils/participantHelpers';

const DyteAudio = ({
  source,
  studentId,
}: {
  source?: string | null;
  studentId?: string | null;
} = {}) => {
  const { meeting } = useRealtimeKitMeeting();

  // States
  const [isMuted, setIsMuted] = useState<boolean>(true);

  // Redux
  const joinedParticipants = useRealtimeKitSelector((m) => m.participants.joined.toArray());
  const audioHandlerRef = useRef<DyteAudioHandler | null>(null);

  // ===== NEW: Parent source audio filtering logic =====
  // This effect filters audio tracks for parent sources only
  useEffect(() => {
    // Only apply filtering for parent source
    if (source !== 'parent' || !audioHandlerRef.current) {
      return;
    }

    // Get allowed participant IDs (tutor and selected student)
    const allowedParticipantIds = new Set<string>();

    joinedParticipants.forEach((participant) => {
      if (shouldIncludeAudioForParent(participant, studentId)) {
        const participantId = participant.id || participant.customParticipantId;
        if (participantId) {
          allowedParticipantIds.add(participantId);
        }
      }
    });

    // Remove audio tracks from participants not in allowed list
    joinedParticipants.forEach((participant) => {
      const participantId = participant.id || participant.customParticipantId;
      if (participantId && !allowedParticipantIds.has(participantId)) {
        // Remove both regular audio and screen share audio
        audioHandlerRef.current?.removeTrack(`audio-${participantId}`);
        audioHandlerRef.current?.removeTrack(`screenshare-${participantId}`);
      }
    });
  }, [joinedParticipants, source, studentId]);
  // ===== END NEW: Parent source audio filtering logic =====

  useEffect(() => {
    audioHandlerRef.current = new DyteAudioHandler();
    return () => {
      if (audioHandlerRef.current) {
        audioHandlerRef.current.dispose();
        audioHandlerRef.current = null;
      }
    };
  }, []);

  const audioUpdateListener = useCallback(
    ({
      id,
      audioEnabled,
      audioTrack,
    }: {
      id: string;
      audioEnabled: boolean;
      audioTrack: MediaStreamTrack;
    }) => {
      // ===== NEW: Parent source filtering check =====
      if (source === 'parent') {
        const participant = joinedParticipants.find(
          (p) => p.id === id || p.customParticipantId === id,
        );
        // Block audio if participant is not tutor or selected student
        if (!participant || !shouldIncludeAudioForParent(participant, studentId)) {
          audioHandlerRef.current?.removeTrack(`audio-${id}`);
          return;
        }
      }
      // ===== END NEW: Parent source filtering check =====

      const audioId = `audio-${id}`;
      if (audioHandlerRef && audioHandlerRef.current) {
        if (audioEnabled && audioTrack != null) {
          audioHandlerRef.current.addTrack(audioId, audioTrack);
        } else {
          audioHandlerRef.current.removeTrack(audioId);
        }
      }
    },
    [audioHandlerRef, source, studentId, joinedParticipants],
  );

  useEffect(() => {
    for (const participant of joinedParticipants) {
      audioUpdateListener(participant);
    }
  }, [audioUpdateListener, joinedParticipants]);

  useEffect(() => {
    const participantLeftListener = ({ id }: { id: string }) => {
      if (audioHandlerRef && audioHandlerRef.current) {
        audioHandlerRef.current.removeTrack(`audio-${id}`);
        audioHandlerRef.current.removeTrack(`screenshare-${id}`);
      }
    };

    const screenShareUpdateListener = ({
      id,
      screenShareEnabled,
      screenShareTracks,
    }: {
      id: string;
      screenShareEnabled: boolean;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      screenShareTracks: any;
    }) => {
      // ===== NEW: Parent source filtering check for screen share =====
      if (source === 'parent') {
        const participant = joinedParticipants.find(
          (p) => p.id === id || p.customParticipantId === id,
        );
        // Block screen share audio if participant is not tutor or selected student
        if (!participant || !shouldIncludeAudioForParent(participant, studentId)) {
          audioHandlerRef.current?.removeTrack(`screenshare-${id}`);
          return;
        }
      }
      // ===== END NEW: Parent source filtering check for screen share =====

      const audioId = `screenshare-${id}`;
      if (audioHandlerRef && audioHandlerRef.current) {
        if (screenShareEnabled && screenShareTracks.audio != null) {
          audioHandlerRef.current.addTrack(audioId, screenShareTracks.audio);
        } else {
          audioHandlerRef.current.removeTrack(audioId);
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deviceUpdateListener = ({ device }: any) => {
      if (device.kind === 'audiooutput') {
        if (audioHandlerRef && audioHandlerRef.current) {
          audioHandlerRef.current.setDevice(device.deviceId);
        }
      }
    };

    meeting.participants.joined.addListener('audioUpdate', audioUpdateListener);
    meeting.participants.joined.addListener('screenShareUpdate', screenShareUpdateListener);
    meeting.participants.joined.addListener('participantLeft', participantLeftListener);
    meeting.self.addListener('deviceUpdate', deviceUpdateListener);
    return () => {
      meeting.participants.joined.removeListener('audioUpdate', audioUpdateListener);
      meeting.participants.joined.removeListener('screenShareUpdate', screenShareUpdateListener);
      meeting.participants.joined.removeListener('participantLeft', participantLeftListener);
      meeting.self.removeListener('deviceUpdate', deviceUpdateListener);
    };
  }, [meeting, audioUpdateListener, source, studentId, joinedParticipants]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { data } = event;
      if (event.data?.type === 'AUDIO_STATUS') {
        setIsMuted(data?.payload);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (audioHandlerRef && audioHandlerRef.current) {
      if (isMuted) {
        // Mute the complete audio tag
        audioHandlerRef.current.muteAudioTag();
      } else {
        // Unmute the complete audio tag
        audioHandlerRef.current.unmuteAudioTag();
      }
    }
  }, [isMuted]);

  return null;
};

export default DyteAudio;
